import { type QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { costEstimateEntityNames, damageAppraisalEntityNames, invoiceEntityNames } from "@workspace/schemas/step-registry";

import { type EClaimEntityType, SEARCH_MIN_QUERY_CHARS, eclaimEntityLookupQueryOptions, normalizeEclaimValue } from "./searchQueryOptions";

// The entity names EntitySearchInput renders, mapped to their eClaim search scope.
// (deviatingPayee has no extracted suggestions, so there is nothing to warm for it.)
const entityTypeByName: Record<string, EClaimEntityType> = {
  [costEstimateEntityNames.CLAIMINI_ID]: "incident",
  [costEstimateEntityNames.GARAGE]: "person",
  [damageAppraisalEntityNames.EXPERT_REPORT_COMPANY]: "person",
  [invoiceEntityNames.PERSONAL_ACCOUNT]: "person",
};

// Tolerant pick of a check step input: only entity name/value pairs, top-level
// and per attachment. Steps with other input shapes (inbox processing, document tasks)
// simply yield no entities — prefetching is best-effort by design.
const entityPickSchema = z.object({ name: z.string(), value: z.union([z.string(), z.number()]) }).loose();
const stepInputPickSchema = z
  .object({
    entities: z.array(entityPickSchema).optional(),
    attachments: z.array(z.object({ entities: z.array(entityPickSchema).optional() }).loose()).optional(),
  })
  .loose();

/**
 * Warm the eClaim entity-search cache for every id the task's check step form will
 * look up on mount (committed values, suggestions, resting labels), so the form renders
 * from cache instead of showing a pending flicker. Fire-and-forget: failures are
 * swallowed and the form's own queries remain the source of truth.
 */
export function prefetchEclaimEntityLookups(queryClient: QueryClient, steps: { input: unknown }[]): void {
  const seen = new Set<string>();
  for (const step of steps) {
    const parsed = stepInputPickSchema.safeParse(step.input);
    if (!parsed.success) continue;

    const entityLists = [parsed.data.entities ?? [], ...(parsed.data.attachments ?? []).map((attachment) => attachment.entities ?? [])];
    for (const entity of entityLists.flat()) {
      const entityType = entityTypeByName[entity.name];
      const value = normalizeEclaimValue(entity.value);
      // prefetchQuery ignores the `enabled` option, so mirror its min-length guard here.
      if (!entityType || value.length < SEARCH_MIN_QUERY_CHARS) continue;

      const key = `${entityType}:${value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      void queryClient.prefetchQuery(eclaimEntityLookupQueryOptions(entityType, value));
    }
  }
}
